import React, { useCallback, useEffect, useState } from 'react'
import { Handle, NodeProps, NodeToolbar, Position, useReactFlow } from '@xyflow/react'
import { ApiVersion } from '../../../../enums/api-version.enum'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationService } from '../../../../services'
import { getEdgeId } from '../../helpers'
import { JourneyNodeIcon } from './journey-node-icon'
import { JourneyNodeLabel } from './journey-node-label'
import { getClassNames } from '../../../../_utils/classnames'
import { useJourneyContext, useEnrichmentCallbacks } from '../../context'
import { parseLoadedVariant } from '../../../../components/notification-builder/helpers'
import { AppState } from '../../../../stores/app'
import { parseDomainNotificationBuildFromVariant } from '../../action-helpers'
import { getItemStrategy, isActionStep, JourneyNode } from '../../types/journey-nodes'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import type { ISendNotificationActionParams } from '@pushly/models/lib/structs/journeys'
import { InvalidJourneyComponent } from '../../edge-types/custom-edge/invalid-journey-component'
import { JourneyNodeInteractionActions } from './journey-node-interaction-actions'
import './custom-node.scss'

import { StepType } from '@pushly/models/lib/enums/journeys'
import { JourneyAction } from '../../types/journey-context'
import { PlaceholderReplacementPicker } from './placeholder-replacement-picker'
import { CONVERGENCE_PLACEHOLDER_SIZE } from '../../constants'

export const CustomNode = ({ id, data, targetPosition, sourcePosition }: NodeProps<JourneyNode>) => {
    const [state, dispatch] = useJourneyContext()
    const enrichment = useEnrichmentCallbacks()
    const { getNode } = useReactFlow<JourneyNode>()
    const appState = useService(AppState)
    const notifSvc = useService(NotificationService)
    const [loadingDetails, setLoadingDetails] = useState(false)

    const step = data.step

    const handleNodeBodyClick = useCallback(() => {
        // Placeholder nodes use a popover picker — don't close it on the same click
        if (step.type === StepType.PLACEHOLDER) return
        // Close popovers when clicking node body (not buttons)
        dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
    }, [dispatch, step.type])

    // onComponentMount effect - fetch step details dependencies
    useEffect(() => {
        const getAction = async () => {
            if (!isActionStep(step)) return

            const actionConfig = step.configuration
            const notificationId = actionConfig.params.notification_id
            if (!notificationId) return

            enrichment?.onEnrichStart()
            setLoadingDetails(true)

            try {
                const notif = await notifSvc.fetchNotificationById(state.domain.id, notificationId, {
                    query: { include_schedules: false },
                    version: ApiVersion.V4,
                })
                if (!notif) return

                const variant = parseLoadedVariant(notif)
                const createReq = parseDomainNotificationBuildFromVariant(variant, appState.flags, state.domain)

                const itemStrategy = getItemStrategy(step)

                // Only patch common notification fields; preserve existing params
                // Update the tracked data and push into the flow store
                const updatedStep = updateStepConfig(step, {
                    params: {
                        ...actionConfig.params,
                        notification: createReq,
                        notification_id: notificationId,
                        ...(itemStrategy ? { item_strategy: itemStrategy } : {}),
                    } as ISendNotificationActionParams,
                })

                dispatch({ action: JourneyAction.ON_NODE_ENRICH, data: { nodeId: id, enrichedStep: updatedStep } })
            } finally {
                setLoadingDetails(false)
                enrichment?.onEnrichEnd()
            }
        }

        getAction()
    }, [])

    const isPlaceholder = step.type === StepType.PLACEHOLDER
    const placeholderRole = data.uiMeta?.placeholderRole
    const isConvergence = isPlaceholder && placeholderRole === 'convergence'

    // Convergence placeholders render as a minimal dot/indicator — no card, no replacement picker.
    // The wrapper is sized to match the dot so React Flow's edge connection points align correctly.
    if (isConvergence) {
        return (
            <div style={{ width: CONVERGENCE_PLACEHOLDER_SIZE, height: CONVERGENCE_PLACEHOLDER_SIZE, position: 'relative' }}>
                <Handle type="target" position={targetPosition ?? Position.Top} id={getEdgeId(id)} style={{ opacity: 0, width: 0, height: 0, top: 0 }} />
                <div className="convergence-dot" />
                <Handle type="source" position={sourcePosition ?? Position.Bottom} id={getEdgeId(id)} style={{ opacity: 0, width: 0, height: 0, bottom: 0 }} />
            </div>
        )
    }


    const nodeBody = (
        <div
            className={getClassNames(
                null,
                `custom-node-wrapper type-${step.type} ${
                    step.configuration.type ? `subType-${String(step.configuration.type).toLowerCase()}` : ''
                }`,
                {
                    invalid: data.uiMeta.invalid,
                    'placeholder-role-empty': isPlaceholder && placeholderRole === 'empty',
                    'placeholder-role-convergence': isPlaceholder && placeholderRole === 'convergence',
                },
            )}
            onClick={handleNodeBodyClick}
        >
            <div className="custom-node-border" />
            <div className={getClassNames(null, 'custom-node', 'no-drag')}>
                {isPlaceholder ? (
                    placeholderRole === 'empty' ? (
                        <span className="placeholder-label">Add your first step</span>
                    ) : <>&nbsp;</>
                ) : (
                    <>
                        <JourneyNodeIcon id={id} node={step} />
                        <JourneyNodeLabel node={step} loading={loadingDetails} />
                        {![StepType.TRIGGER, StepType.EXIT].includes(step.type) && (
                            <JourneyNodeInteractionActions nodeId={id} enriching={loadingDetails} />
                        )}
                    </>
                )}
            </div>
        </div>
    )

    return (
        <React.Fragment>
            {![StepType.TRIGGER].includes(step.type) && (
                <Handle type="target" position={targetPosition ?? Position.Top} id={getEdgeId(id)} />
            )}
            <NodeToolbar isVisible={!!data.uiMeta.invalid} position={Position.Left} className="journey-invalid-label">
                <InvalidJourneyComponent content={data.uiMeta.invalid?.reason} />
            </NodeToolbar>
            {isPlaceholder ? (
                <PlaceholderReplacementPicker node={getNode(id)!}>
                    {nodeBody}
                </PlaceholderReplacementPicker>
            ) : (
                nodeBody
            )}
            {step.type !== StepType.EXIT && (
                <Handle type="source" position={sourcePosition ?? Position.Bottom} id={getEdgeId(id)} />
            )}
        </React.Fragment>
    )
}
